import { useI18n } from 'context/i18n';
import { DialogTrigger } from 'react-aria-components';

import { Drawer as DrawerLab, DrawerRoot, DrawerHeader } from 'components/ui/drawer';
import { Select, SelectItem } from 'components/ui/select';
import { Button } from 'components/ui/button';

import { dataLayerHandleEvent } from 'utils/dataLayers';
import { getTarget } from 'utils/helpers';

import { ScrollArea } from 'components/ui/scroll-area';
import { MeasureCamperlab } from './measure';
import { useProductSheet } from 'context/product-sheet';

import { useSizeGuide } from './guide.hooks';

export function LabSizeGuide() {
  const { t } = useI18n();
  const {
    productSheet: { code, brand },
  } = useProductSheet();
  const { currentGuide, selectedTarget, setSelectedTarget, guidesTargets, cols, rows, setUnits, units } = useSizeGuide();

  const handleOnOpen = (open: boolean) => {
    if (open) {
      if (!currentGuide) return;
      dataLayerHandleEvent({
        event: 'item_size_guide',
        event_category: 'product detail',
        event_detail_1: 'open',
        event_detail_2: getTarget(currentGuide.targetId, 2),
      });

      dataLayerHandleEvent({
        event: 'shoe_size_guide',
        item_name: brand?.toLowerCase(),
        item_id: code,
      });
      return;
    }
    dataLayerHandleEvent({
      event: 'item_size_guide',
      event_category: 'product detail',
      event_detail_1: 'close',
      event_detail_2: '',
    });
  };

  return (
    <>
      <DialogTrigger onOpenChange={handleOnOpen}>
        <Button variant="none" className="text-xs font-bold uppercase underline-offset-2 lg:text-xxs">
          <span className="underline">{t('ficha.producto', 'size.fit')}</span>
        </Button>
        <DrawerRoot className="w-full">
          <DrawerLab>
            <ScrollArea
              className="relative flex h-[100dvh] max-h-[100dvh] max-w-[100vw] flex-grow flex-col overflow-hidden px-2.5 [&>div>div]:flex [&>div>div]:flex-col [&>div]:flex-col"
              transparent
            >
              <DrawerHeader label={t('ficha.producto', 'size.fit')} />
              <div className="mb-2 flex justify-start empty:hidden">
                {guidesTargets.length > 1 ?
                  <Select variant="default" selectedKey={selectedTarget} onSelectionChange={(key) => setSelectedTarget(key as string)} items={guidesTargets}>
                    {(item) => (
                      <SelectItem key={item.id} id={item.id} className="capitalize">
                        {item.name}
                      </SelectItem>
                    )}
                  </Select>
                : null}
              </div>
              <table className="mb-5 w-full text-sm lg:text-xs">
                <thead>
                  <tr>
                    {cols.map((column) => (
                      <th key={column.key} className={`px-1 py-0.5 pb-2 first:pl-0 last:pr-0 ${column.className ?? ''}`}>
                        {column.render ? column.render(column.title) : column.title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="pt-2">
                  {rows.map((row, idx) => (
                    <tr key={idx}>
                      {row.map((cell) => (
                        <td key={cell.key} className={`px-1 py-0.5 first:pl-0 last:pr-0 ${cell.className ?? ''}`}>
                          {cell.render ? cell.render(cell.rowData) : cell.value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mb-20 flex gap-4">
                <button onClick={() => setUnits('cm')} className={`text-sm lowercase md:text-xs ${units === 'cm' ? 'underline' : ''}`}>
                  {t('ficha.producto', 'size.units.cm', 'cm')}
                </button>
                <button onClick={() => setUnits('in')} className={`text-sm lowercase md:text-xs ${units === 'in' ? 'underline' : ''}`}>
                  {t('ficha.producto', 'size.units.in', 'in')}
                </button>
              </div>
              <div className="mt-20">
                <MeasureCamperlab type={currentGuide.productType} />
              </div>
            </ScrollArea>
          </DrawerLab>
        </DrawerRoot>
      </DialogTrigger>
    </>
  );
}
